<template>
    <div>
        <v-card class="beginning-inventory-form" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col class="d-flex align-center" cols="12" sm="9">
                        <v-btn dark icon @click="goBack">
                            <v-icon>fal fa-arrow-left</v-icon>
                        </v-btn>
                        <div class="ml-3">{{ $t('beginning_inventories.heading') }}</div>
                    </v-col>

                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            v-if="!disabled && !form.recorded_at"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="save(false)"
                        >
                            <v-icon left>fal fa-check</v-icon>
                            {{ $t('beginning_inventories.save') }}
                        </v-btn>

                        <v-btn
                            v-if="!form.recorded_at"
                            class="mx-3"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="record"
                        >
                            <v-icon left>fal fa-file-check</v-icon>
                            {{ $t('beginning_inventories.record') }}
                        </v-btn>

                        <v-btn
                            v-if="form.recorded_at"
                            class="mx-3"
                            dark
                            outlined
                            :disabled="!form.items.length"
                            @click="rollback"
                        >
                            <v-icon left>fal fa-file-times</v-icon>
                            {{ $t('beginning_inventories.rollback') }}
                        </v-btn>

                        <v-menu bottom transition="slide-y-transition">
                            <template v-slot:activator="{ on }">
                                <v-btn dark v-on="on" icon>
                                    <v-icon>far fa-ellipsis-v</v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    key="print"
                                    :disabled="!beginningInventory || !beginningInventory.recorded_at"
                                    @click="print"
                                >
                                    <v-list-item-action class="mx-3">
                                        <v-icon :disabled="!beginningInventory || !beginningInventory.recorded_at">
                                            fal fa-print
                                        </v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>
                                        {{ $t('general.print') }}
                                    </v-list-item-title>
                                </v-list-item>

                                <v-divider />

                                <v-list-item
                                    key="import"
                                    :disabled="!beginningInventory || !!beginningInventory.recorded_at"
                                    @click="importDialog = true"
                                >
                                    <v-list-item-action class="mx-3">
                                        <v-icon :disabled="!beginningInventory || !!beginningInventory.recorded_at">
                                            fal fa-upload
                                        </v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>
                                        {{ $t('general.import') }}
                                    </v-list-item-title>
                                </v-list-item>

                                <v-divider />

                                <v-list-item
                                    key="export"
                                    :disabled="!beginningInventory"
                                    @click="exportToFile"
                                >
                                    <v-list-item-action class="mx-3">
                                        <v-icon :disabled="!beginningInventory">
                                            fal fa-download
                                        </v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>
                                        {{ $t('general.export') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-row class="mt-3 mx-0">
                    <v-col cols="12" md="4">
                        <div class="highlight-section">
                            <div class="section-title">
                                <v-icon class="mr-3">fal fa-file-alt</v-icon>
                                {{ $t('beginning_inventories.section_document_info') }}
                            </div>

                            <div class="section-content">
                                <v-row>
                                    <v-col cols="6">
                                        <v-menu
                                            v-model="picker1"
                                            :close-on-content-click="false"
                                            :nudge-right="32"
                                            transition="scale-transition"
                                            min-width="290"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                    v-model="form.date"
                                                    :error-messages="form.$errors.date"
                                                    :label="$t('general.date')"
                                                    prepend-icon="fal fa-calendar"
                                                    dense
                                                    readonly
                                                    v-on="on"
                                                    :disabled="disabled || !!form.items.length"
                                                    @blur="form.validate('date')"
                                                    @input="delete form.$errors.date"
                                                />
                                            </template>
                                            <v-date-picker
                                                v-model="form.date"
                                                @input="picker1 = false; form.validate('date')"
                                            />
                                        </v-menu>
                                    </v-col>

                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="form.number"
                                            :label="$t('beginning_inventories.number')"
                                            dense
                                            :disabled="disabled"
                                            :error-messages="form.$errors.number"
                                            @blur="form.validate('number')"
                                            @input="delete form.$errors.number;"
                                        />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-textarea
                                            v-model="form.description"
                                            class="ma-0"
                                            :label="$t('general.description')"
                                            dense
                                            auto-grow
                                            :rows="1"
                                            :disabled="disabled"
                                            :error-messages="form.$errors.description"
                                            @blur="form.validate('description')"
                                            @input="delete form.$errors.description"
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                        </div>
                    </v-col>

                    <v-col cols="12" md="4">
                        <div class="highlight-section">
                            <div class="section-title mb-4">
                                <v-icon class="mr-3">fal fa-sigma</v-icon>
                                {{ $t('beginning_inventories.section_summary') }}
                            </div>

                            <div class="section-content">
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('beginning_inventories.selling_value') }}:</span>
                                    <span>{{ beginningInventoryInternal.getSellingValue().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('beginning_inventories.tax') }}:</span>
                                    <span>{{ beginningInventoryInternal.getTax().toFormat('0.00') }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                    <span>{{ $t('beginning_inventories.selling_value_after_tax') }}:</span>
                                    <span>{{ beginningInventoryInternal.getSellingValueAfterTax().toFormat('0.00') }}</span>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="form.items"
                    :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:header.actions="{ on, props }">
                        <div v-if="!disabled" class="d-flex justify-end">
                            <v-btn icon large color="primary" @click="openItemForm()">
                                <v-icon>fal fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </template>

                    <template v-slot:item="{ item, index }">
                        <tr>
                            <td>{{ index + 1 }}</td>
                            <td>{{ item.getProduct() && item.getProduct().code ? item.getProduct().code : '' }}</td>
                            <td>{{ item.getProduct() ? item.getProduct().name : '' }}</td>
                            <td>{{ item.getQuantity() }} <small>{{ item.getUnitOfMeasure() }}</small></td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingPrice().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingPriceAfterTax().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValue().toFormat('0.00') }}</td>
                            <td>{{ item.getTax().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValueAfterTax().toFormat('0.00') }}</td>

                            <td align="center" class="w35 maxw60">
                                <v-menu
                                    v-if="!disabled"
                                    bottom
                                    transition="slide-y-transition"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item :key="1" @click="openItemForm(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item :key="2" @click="deleteItem(item)">
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Item form -->
        <beginning-inventory-item-edit
            :show.sync="showItemForm"
            :item="selectedItem"
            :products="products"
            :pricelists="pricelists"
            :date="form.date"
            @save="onItemSave"
        />

        <cod-dialog ref="dialog" :show.sync="importDialog">
            <div slot="header">
                {{ $t('general.import') }} {{ $t('beginning_inventories.heading').toLowerCase() }}
            </div>

            <div slot="content" class="pa-4">
                <v-form ref="importForm">
                    <v-file-input
                        v-model="importFile"
                        class="mt-5"
                        accept=".xlsx, .xls, .csv"
                        :label="$t('products.import.file')"
                        :rules="[CodForm.rule('required')]"
                        clearable
                        outlined
                        prepend-inner-icon="fal fa-paperclip"
                        prepend-icon=""
                        clear-icon="fal fa-times"
                    />
                </v-form>
            </div>>

            <div slot="footer" class="d-flex justify-end pa-4 wfull">
                <v-btn 
                    color="primary"
                    :loading="importingFile"
                    @click="importFromFile"
                >
                    {{ $t('general.import') }}
                </v-btn>
            </div>
        </cod-dialog>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import Product from '@/models/Product';
import BeginningInventory from '@/models/BeginningInventory';
import BeginningInventoryItemEdit from '@/views/shared/admin/beginningInventories/BeginningInventoryItemEdit';
import { required } from 'vuelidate/lib/validators';
import HtmlGen from '@/wrappers/HtmlGen';
import CodDialog from '@/components/CodDialog';

export default {
    components: {
        BeginningInventoryItemEdit,
        CodDialog,
    },

    async created() {
        let url = '/admin/load/beginning-inventory' + (this.$route.params.id ? '/' + this.$route.params.id : '');

        const data = await loadData(url);

        if(data.beginningInventory) {
            this.beginningInventory = new BeginningInventory(data.beginningInventory);
        }

        this.nextDocumentNumber = data.maxDocumentNumber ? parseInt(data.maxDocumentNumber) + 1 : 1;
        this.products = data.products.map(p => new Product(p));
        this.pricelists = data.pricelists;
        this.form = this.createForm();
    },

    data() {
        return {
            beginningInventory: null,
            nextDocumentNumber: null,
            products: [],
            pricelists: [],
            form: this.createForm(),
            showItemForm: false,
            headers: [
                { text: this.$t('general.number'), value: 'product.number', sortable: false },
                { text: this.$t('general.code'), value: 'product.code', sortable: false },
                { text: this.$t('general.inventory_item'), value: 'name', sortable: false },
                { text: this.$t('beginning_inventories.quantity'), value: 'quantity', sortable: false },
                { text: this.$t('beginning_inventories.selling_price'), value: 'selling_price', sortable: false },
                { text: this.$t('beginning_inventories.selling_price_after_tax'), value: 'selling_price_after_tax', sortable: false },
                { text: this.$t('beginning_inventories.selling_value'), value: 'selling_value', sortable: false },
                { text: this.$t('beginning_inventories.tax'), value: 'tax', sortable: false },
                { text: this.$t('beginning_inventories.selling_value_after_tax'), value: 'selling_value_after_tax', sortable: false },
                { text: '', value: 'actions', sortable: false }
            ],

            selectedItem: null,
            picker1: false,
            picker2: false,
            beginningInventoryNumberPrefix: moment().format('YY/'),
            
            importDialog: false,
            importFile: null,
            importingFile: false,
        };
    },

    validations: {
        form: {
            number: { required },
            date: { required },
        }
    },

    computed: {
        beginningInventoryInternal() {
            return new BeginningInventory(this.form.toJson());
        },

        disabled() {
            return !!this.form.recorded_at;
        }
    },

    watch: {
        importDialog(val) {
            if (!val) {
                this.$refs.importForm.reset();
                this.importFile = null;
            }
        }
    },

    methods: {
        createForm() {
            return new Form(this, 'form', {
                id: this.beginningInventory?.id || null,
                user_id: this.user?.id || null,
                number: this.beginningInventory?.number || this.beginningInventoryNumberPrefix + this.nextDocumentNumber,
                date: this.beginningInventory?.date
                    ? moment(this.beginningInventory.date).format('YYYY-MM-DD')
                    : moment().format('YYYY-MM-DD'),
                description: this.beginningInventory?.description || null,
                items: this.beginningInventory?.items || [],
                recorded_at: this.beginningInventory?.recorded_at || null,
            });
        },

        openItemForm(item = null) {
            if(!this.form.validate()) return;

            this.selectedItem = item;
            this.showItemForm = true;
        },

        deleteItem(item) {
            this.form.items = this.form.items.filter(i => i !== item);
            this.showItemForm = false;
        },

        onItemSave(item) {
            let isEdit = this.form.items.some(i => i.hash === item.hash);
            if(!isEdit) {
                this.form.items.push(item);
                return;
            }

            this.form.items.forEach((i, index, array) => {
                if (i.hash === item.hash) {
                    this.$set(array, index, item);
                }
            });
        },

        async save(recordPending = false) {
            if(!this.form.validate()) return;

            if (!this.form.id) {
                const response = await ApiService.post('/admin/beginning-inventories', this.form);
                this.beginningInventory = new BeginningInventory(response.data);
                this.form = this.createForm();

                if(recordPending) return;

                this.$snackbar(this.$t('beginning_inventories.saved'), 'success');
            } else {
                const response = await ApiService.patch(`/admin/beginning-inventories/${this.form.id}`, this.form);
                this.beginningInventory = new BeginningInventory(response.data);
                this.form = this.createForm();

                if(recordPending) return;

                this.$snackbar(this.$t('beginning_inventories.updated'), 'success');
            }

            this.goBack();
        },

        async record() {
            await this.save(true);

            const response = await ApiService.post(`/admin/beginning-inventories/${this.form.id}/record`, {})
            if(response.data.products?.length) {
                Product.insert({ data: response.data.products, freeze: true });
            }

            this.$snackbar(this.$t('beginning_inventories.recorded'), 'success');

            this.goBack();
        },

        async rollback() {
            if(!this.form.id) return;

            const response = await ApiService.post(`/admin/beginning-inventories/${this.form.id}/rollback`, {})
            if(response.data.products?.length) {
                Product.insert({ data: response.data.products, freeze: true });
            }

            this.$snackbar(this.$t('beginning_inventories.rolled_back'), 'success');

            this.goBack();
        },

        print() {
            const html = this.beginningInventory.toTextLines().map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        async importFromFile() {
            this.importingFile = true;

            if (!this.$refs.importForm.validate()) return false;

            let formData = new FormData();
            formData.append('beginning-inventory', this.importFile);

            try {
                const response = await ApiService.post(`/admin/beginning-inventories/${this.beginningInventory.id}/import`, formData, {
                    transformRequest: (data, headers) => {
                        headers.common['Content-Type'] = 'multipart/form-data';
                        return data;
                    },
                    timeout: 150000,
                });

                this.$snackbar(this.$t('beginning_inventories.imported'), 'success');

                this.beginningInventory = new BeginningInventory(response.data);
                this.importDialog = false;
                this.form = this.createForm();

            } catch (e) {
                const errorCode = e.response?.data?.errors?.[0]?.code || 'unknown_error';
                this.$snackbar(this.$t(`beginning_inventories.error.${errorCode}`), 'error');
            } finally {
                this.importingFile = false;
            }
        },

        async exportToFile() {
            const response = await ApiService.get(`/admin/beginning-inventories/${this.beginningInventory.id}/export`, { responseType: 'blob' });
            const fileBlob = new Blob([response.data]);
            const href = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'beginning-inventory.xlsx');
            document.body.appendChild(link);
            link.click();
        },

        goBack() {
            this.$router.push({ name: 'adminBeginningInventories' }).catch(err => {});
        },
    }
};
</script>

<style lang="scss" scoped>
.beginning-inventory-form {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    .highlight-section {
        border-radius: 3px;
        background: #fafafa;
        border: 1px solid #cccccc;
        padding: 20px;
        min-height: 215px;
        font-size: 15px;

        .section-title {
            margin-bottom: 24px;
            font-size: 20px;
            color: black;

            .v-icon {
                color: black;
            }
        }
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }
    }
}
</style>
